import { Avatar, Button, Form, Input, Select, Upload } from 'antd';
import { UploadOutlined, UserOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useAuthStore } from '@presentation/stores/authStore';
import { useDeleteAvatar, useUpdateProfile, useUploadAvatar } from '@presentation/hooks/useProfile';
import { useAppMessage } from '@presentation/hooks/useAppMessage';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors } from '@shared/utils/formErrors';

// `Intl.supportedValuesOf('timeZone')` doesn't enumerate the "UTC" alias
// itself (only IANA zone names like "Etc/UTC"), even though it's a valid
// timeZone value. Without it, a Select value="UTC" would fail to match any
// option — prepending it explicitly keeps the option in sync with the DB
// column's default.
const TIMEZONES = ['UTC', ...(Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : [])];

interface ProfileFormValues {
  name: string;
  timezone: string;
}

export const ProfileTab = () => {
  const user = useAuthStore((state) => state.user);
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();
  const [form] = Form.useForm<ProfileFormValues>();
  const message = useAppMessage();

  const handleFinish = (values: ProfileFormValues) => {
    updateProfile.mutate(values, {
      onSuccess: () => message.success('Profile updated.'),
      onError: (error) => {
        if (error instanceof ValidationError) {
          setAntdFormErrors(form, error.errors);
        }
      },
    });
  };

  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    uploadAvatar.mutate(file, {
      onSuccess: () => message.success('Avatar updated.'),
      onError: (error) => message.error(error.message),
    });
    return false;
  };

  return (
    <div className="max-w-md space-y-8">
      <div className="flex items-center gap-4">
        <Avatar size={64} src={user?.avatarUrl} icon={<UserOutlined />} />
        <div className="flex gap-2">
          <Upload
            accept="image/png,image/jpeg,image/webp"
            showUploadList={false}
            beforeUpload={beforeUpload}
          >
            <Button icon={<UploadOutlined />} loading={uploadAvatar.isPending}>
              Upload avatar
            </Button>
          </Upload>
          {user?.avatarUrl && (
            <Button
              danger
              type="text"
              loading={deleteAvatar.isPending}
              onClick={() =>
                deleteAvatar.mutate(undefined, {
                  onSuccess: () => message.success('Avatar removed.'),
                })
              }
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{ name: user?.name, timezone: user?.timezone ?? 'UTC' }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="timezone" label="Timezone">
          <Select
            showSearch
            options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={updateProfile.isPending}>
          Save profile
        </Button>
      </Form>
    </div>
  );
};
